import { createContext, useContext, useEffect } from 'react';

/**
 * Реестр полей формы: FormDialog знает, какие поля отрисованы, и подставляет ошибки 422 только в
 * существующие поля (остальные — плашкой над формой).
 *
 * `register(name)` — контрол формы с именем поля контракта; `register(name, target)` — поле
 * контракта без своего контрола (например, `user_id` поиска пользователя): его ошибки показываются
 * под контролом `target`.
 */
export interface FieldRegistry {
  register: (name: string, target?: string) => () => void;
}

export const FieldRegistryContext = createContext<FieldRegistry | null>(null);

/**
 * Зарегистрировать поле в окне формы, чтобы ошибка сервера 422 по нему легла под поле, а не в
 * плашку. Нужен собственным полям слайсов (поля `shared/ui` регистрируются сами). `target` — имя
 * контрола, под которым показать ошибку поля без своего контрола.
 */
export function useRegisterField(name: string, target?: string): void {
  const registry = useContext(FieldRegistryContext);
  useEffect(() => registry?.register(name, target), [registry, name, target]);
}

/** Несколько имён одного контрола (например, языки LocalizedText: `title.en`, `title.es`). */
export function useRegisterFields(names: readonly string[]): void {
  const registry = useContext(FieldRegistryContext);
  // Имена сравниваются по значению: новый массив с теми же именами не перерегистрирует поле.
  const key = names.join('\n');
  useEffect(() => {
    if (!registry || key === '') return undefined;
    const releases = key.split('\n').map((name) => registry.register(name));
    return () => {
      for (const release of releases) release();
    };
  }, [registry, key]);
}

/**
 * Несколько полей контракта без своего контрола → контролы формы (`map_zone.id` → `zone_choice`,
 * `media.0.file_id` → `cover_file_id`). Набор сравнивается по значению, как в {@link useRegisterFields}.
 */
export function useRegisterAliases(aliases: Readonly<Record<string, string>>): void {
  const registry = useContext(FieldRegistryContext);
  const key = Object.entries(aliases)
    .map(([name, target]) => `${name}\t${target}`)
    .sort()
    .join('\n');
  useEffect(() => {
    if (!registry || key === '') return undefined;
    const releases = key.split('\n').map((line) => {
      const [name = '', target = ''] = line.split('\t');
      return registry.register(name, target);
    });
    return () => {
      for (const release of releases) release();
    };
  }, [registry, key]);
}

/** Счётчик регистраций: одно имя могут зарегистрировать несколько контролов (смена ключа и т. п.). */
export class FieldNames {
  private readonly counts = new Map<string, number>();
  private readonly targets = new Map<string, string>();

  register(name: string, target?: string): () => void {
    if (target && target !== name) {
      this.targets.set(name, target);
      return () => {
        if (this.targets.get(name) === target) this.targets.delete(name);
      };
    }
    this.counts.set(name, (this.counts.get(name) ?? 0) + 1);
    return () => {
      const left = (this.counts.get(name) ?? 1) - 1;
      if (left > 0) this.counts.set(name, left);
      else this.counts.delete(name);
    };
  }

  /** Отрисованные контролы. */
  get names(): ReadonlySet<string> {
    return new Set(this.counts.keys());
  }

  /** Поля контракта без своего контрола → контрол, под которым показать их ошибку. */
  get aliases(): ReadonlyMap<string, string> {
    return new Map(this.targets);
  }
}
